import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import find from 'lodash/find';

import { IAnimationOptions } from './contentAnimation';
import { ContentMaterial } from './contentMaterial';
import { PatternModel } from './Pattern/Group/patternModel';
import { SystemLayer } from '../System/systemLayer';
import { IContentOptions } from './content';

const playModelAnimation = (animation: IAnimationOptions, loop?: boolean): void => {
    const anim = animation.modelanim;
    anim.start(loop);
};

const pauseModelAnimation = (animation: IAnimationOptions): void => {
    const anim = animation.modelanim;
    anim.pause();
};

const stopModelAnimation = (animation: IAnimationOptions) => {
    const anim = animation.modelanim;
    anim.stop();
};

const goToFirstFrame = (animation: IAnimationOptions) => {
    const anim = animation.modelanim;
    anim.play();
    anim.goToFrame(anim.from);
};

const goToLastFrame = (animation: IAnimationOptions) => {
    const anim = animation.modelanim;
    anim.play();
    anim.goToFrame(anim.to);
    anim.pause();
};

const goToFramePercentage = (animation: IAnimationOptions, perc: number) => {
    perc = animation.anim.easing.ease(perc);
    const anim = animation.modelanim;
    const frame = (anim.to - anim.from) * perc;

    anim.play();
    anim.goToFrame(frame);
    anim.pause();
};

export abstract class ContentModelAnimation extends ContentMaterial {
    public patternModel: PatternModel;

    protected modelAnimations: AnimationGroup[] = [];

    protected modelAnimationsName: string[] = [];

    constructor(systemLayer: SystemLayer, content: IContentOptions) {
        super(systemLayer, content, 'model');
        this.patternModel = this.pattern as PatternModel;
    }

    public setModelAnimations(animations: AnimationGroup[]): void {
        this.modelAnimations = animations;
        this.modelAnimations.forEach((modelAnimation) => {
            this.modelAnimationsName.push(modelAnimation.name);
        });
        this.stopAllModelAnimations();
    }

    public resetGeometry(): void {
        this.stopAllModelAnimations();
        this._resetGeometry();
    }

    public playFirstAnimation(): void {
        if (this.hasModelAnimation) {
            this._system.setNeedProcess(false);
            this._system.startRender();
            this.modelAnimations[0].play(true);
        }
    }

    public hasModelAnimation(): boolean {
        return this.modelAnimations && this.modelAnimations.length !== 0;
    }

    public stopAllModelAnimations(): void {
        this.modelAnimations.forEach((modelAnimation) => {
            modelAnimation.goToFrame(0);
            modelAnimation.stop();
        });
    }

    protected animateWay(contentAnim: IAnimationOptions): void {
        if (!this.hasModelAnimation()) return;
        if (!contentAnim.model) {
            this._animateWay(contentAnim);
        } else {
            const anim = find(this.modelAnimations, (anim) => anim.name === contentAnim.model);
            if (anim) {
                contentAnim.modelanim = anim;
                if (contentAnim.way === 'once') {
                    playModelAnimation(contentAnim);
                } else if (contentAnim.way === 'alternate') {
                    playModelAnimation(contentAnim);
                } else if (contentAnim.way === 'loop') {
                    playModelAnimation(contentAnim, true);
                }

                if (contentAnim.duration) {
                    contentAnim.timeout = setTimeout(() => {
                        stopModelAnimation(contentAnim);
                    }, contentAnim.duration * 1000);
                }
            }
        }
    }

    public animateScroll(contentAnim: IAnimationOptions, perc: number): void {
        if (!contentAnim.model) {
            this._animateScroll(contentAnim, perc);
        } else {
            if (!contentAnim.modelanim) {
                const anim = find(this.modelAnimations, (anim) => anim.name === contentAnim.model);
                if (!anim) return;
                contentAnim.modelanim = anim;
            }
            if (perc === 0) {
                goToFirstFrame(contentAnim);
                stopModelAnimation(contentAnim);
            } else if (perc === 1) {
                goToLastFrame(contentAnim);
                stopModelAnimation(contentAnim);
            } else {
                const loop = (contentAnim.way === 'loop');
                if (!contentAnim.launched) {
                    if (loop) playModelAnimation(contentAnim, loop);
                    else {
                        playModelAnimation(contentAnim, loop);
                        pauseModelAnimation(contentAnim);
                    }
                }
                if (!loop) goToFramePercentage(contentAnim, perc);
            }
        }
    }

    public bumpFollowCamera(meshName: string): void {
        const patternMesh = find(this.patternModel.patternMeshes, (m) => meshName === m.mesh.name);
        const { mesh } = patternMesh;
        const mat = mesh.material;
        const bump = mat.bumpTexture;
        let camPreviusAlpha = this._system.arcCamera.alpha;
        let camPreviusBeta = this._system.arcCamera.beta;

        this._system.scene.registerBeforeRender(() => {
            const camAlpha = this._system.arcCamera.alpha;
            const camBeta = this._system.arcCamera.beta;
            const camChangeAlpha = camPreviusAlpha - camAlpha;
            const camChangeBeta = camPreviusBeta - camBeta;
            bump.vScale = 1 + -camChangeBeta * 2;
            bump.uScale = 1 + camChangeAlpha * 2;
            bump.level = 1 + Math.abs((camChangeAlpha + camChangeBeta) * 50);
            camPreviusAlpha = camAlpha;
            camPreviusBeta = camBeta;
        });
    }
}
